import { useState, useEffect } from 'react';
import { apiRequest, getImageUrl } from '../../utils/api';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Dietary tag definitions
const DIETARY_TAGS = [
    { key: 'is_vegan', label: 'Vegan', icon: '🌱', color: 'bg-green-100 text-green-700' },
    { key: 'is_vegetarian', label: 'Vejetaryen', icon: '🥬', color: 'bg-green-100 text-green-600' },
    { key: 'is_gluten_free', label: 'Glutensiz', icon: '🌾', color: 'bg-yellow-100 text-yellow-700' },
    { key: 'contains_gluten', label: 'Gluten İçerir', icon: '⚠️', color: 'bg-amber-100 text-amber-700' },
    { key: 'is_spicy', label: 'Acı', icon: '🌶️', color: 'bg-red-100 text-red-700' },
    { key: 'is_sweet', label: 'Tatlı', icon: '🍯', color: 'bg-pink-100 text-pink-700' },
    { key: 'is_new', label: 'Yeni', icon: '✨', color: 'bg-blue-100 text-blue-700' },
    { key: 'is_popular', label: 'Popüler', icon: '🔥', color: 'bg-orange-100 text-orange-700' },
    { key: 'is_chef_recommended', label: 'Şef Önerisi', icon: '👨‍🍳', color: 'bg-purple-100 text-purple-700' }
];

const ALLERGENS = [
    { key: 'gluten', label: 'Gluten' },
    { key: 'dairy', label: 'Süt Ürünleri' },
    { key: 'eggs', label: 'Yumurta' },
    { key: 'fish', label: 'Balık' },
    { key: 'shellfish', label: 'Kabuklu Deniz Ürünleri' },
    { key: 'tree_nuts', label: 'Kabuklu Yemişler' },
    { key: 'peanuts', label: 'Fıstık' },
    { key: 'soy', label: 'Soya' },
    { key: 'sesame', label: 'Susam' },
    { key: 'celery', label: 'Kereviz' },
    { key: 'mustard', label: 'Hardal' },
    { key: 'sulfites', label: 'Sülfitler' }
];

// Multi-select category component with search
const CategoryMultiSelect = ({ categories, selectedIds, onChange }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    
    // Filter categories based on search
    const filteredCategories = categories.filter(cat => 
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Get selected categories
    const selectedCategories = categories.filter(cat => selectedIds.includes(cat.id));
    
    const toggleCategory = (categoryId) => {
        if (selectedIds.includes(categoryId)) {
            onChange(selectedIds.filter(id => id !== categoryId));
        } else {
            onChange([...selectedIds, categoryId]);
        }
    };
    
    const removeCategory = (categoryId, e) => {
        e.stopPropagation();
        onChange(selectedIds.filter(id => id !== categoryId));
    };
    
    return (
        <div className="relative">
            {/* Selected Tags */}
            <div 
                className="min-h-[42px] w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex flex-wrap gap-1 items-center"
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedCategories.length === 0 ? (
                    <span className="text-gray-400">Kategori seçin...</span>
                ) : (
                    selectedCategories.map(cat => (
                        <span 
                            key={cat.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                        >
                            {cat.name}
                            <button
                                type="button"
                                onClick={(e) => removeCategory(cat.id, e)}
                                className="hover:bg-blue-200 rounded-full p-0.5"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </span>
                    ))
                )}
                <svg 
                    className={`w-5 h-5 text-gray-400 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
            
            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200">
                        <input
                            type="text"
                            placeholder="Kategori ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                    </div>
                    
                    {/* Category List */}
                    <div className="max-h-48 overflow-y-auto">
                        {filteredCategories.length === 0 ? (
                            <div className="p-3 text-center text-gray-500 text-sm">
                                Kategori bulunamadı
                            </div>
                        ) : (
                            filteredCategories.map(cat => (
                                <div
                                    key={cat.id}
                                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                                        selectedIds.includes(cat.id) ? 'bg-blue-50' : ''
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleCategory(cat.id);
                                    }}
                                >
                                    <div className={`w-5 h-5 border rounded flex items-center justify-center ${
                                        selectedIds.includes(cat.id) 
                                            ? 'bg-blue-500 border-blue-500 text-white' 
                                            : 'border-gray-300'
                                    }`}>
                                        {selectedIds.includes(cat.id) && (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="text-sm text-gray-700">{cat.name}</span>
                                    {cat.parent_id && (
                                        <span className="text-xs text-gray-400 ml-auto">
                                            Alt kategori
                                        </span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            
            {/* Click outside to close */}
            {isOpen && (
                <div 
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

// Sortable Image Item Component
const SortableImageItem = ({ image, onDelete, onSetPrimary, getImageUrl }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: image.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const isVideo = image.media_type === 'video';
    const mediaUrl = isVideo ? (image.video_url || image.image_url) : image.image_url;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="relative group cursor-move"
        >
            {isVideo ? (
                <video
                    src={getImageUrl(mediaUrl)}
                    className="w-full h-24 object-cover rounded-lg"
                    controls={false}
                    muted
                />
            ) : (
                <img
                    src={getImageUrl(mediaUrl)}
                    alt="Product media"
                    className="w-full h-24 object-cover rounded-lg"
                    onError={(e) => {
                        e.target.style.display = 'none';
                    }}
                />
            )}
            
            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onSetPrimary}
                        className={`bg-white text-gray-700 rounded-full w-8 h-8 flex items-center justify-center text-xs hover:bg-blue-100 ${image.is_primary ? 'bg-blue-600 text-white' : ''}`}
                        title={image.is_primary ? 'Ana görsel' : 'Ana görsel yap'}
                    >
                        {image.is_primary ? '⭐' : '☆'}
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-700"
                        title="Sil"
                    >
                        ×
                    </button>
                </div>
            </div>
            
            {/* Badges */}
            {image.is_primary && (
                <span className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Ana
                </span>
            )}
            {isVideo && (
                <span className="absolute top-1 right-1 bg-purple-600 text-white text-xs px-2 py-1 rounded">
                    🎥
                </span>
            )}
            
            {/* Drag handle */}
            <div
                {...attributes}
                {...listeners}
                className="absolute bottom-1 left-1 bg-gray-800 bg-opacity-75 text-white rounded p-1 cursor-grab active:cursor-grabbing"
                title="Sürükle-bırak ile sırala"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
            </div>
        </div>
    );
};

const ProductFormModal = ({ isOpen, onClose, product, organizationId, categoryId, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        currency: 'TRY',
        category_ids: categoryId ? [categoryId] : [],
        status: 'active',
        time_based_active: false,
        start_time: '',
        end_time: '',
        active_days: [],
        // Dietary tags
        is_vegan: false,
        is_vegetarian: false,
        is_gluten_free: false,
        contains_gluten: false,
        is_spicy: false,
        spicy_level: 0,
        is_sweet: false,
        is_new: false,
        is_popular: false,
        is_chef_recommended: false,
        // Nutrition info
        calories: '',
        protein: '',
        carbs: '',
        fat: '',
        allergens: [],
        preparation_time: '',
        // Discount fields
        discount_price: '',
        discount_start_date: '',
        discount_end_date: '',
        discount_start_time: '',
        discount_end_time: '',
        discount_days: []
    });
    const [categories, setCategories] = useState([]);
    const [images, setImages] = useState([]); // New files to upload
    const [imagePreviews, setImagePreviews] = useState([]); // New file previews
    const [existingImages, setExistingImages] = useState([]); // Existing images from server
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const days = [
        { value: 'monday', label: 'Pazartesi' },
        { value: 'tuesday', label: 'Salı' },
        { value: 'wednesday', label: 'Çarşamba' },
        { value: 'thursday', label: 'Perşembe' },
        { value: 'friday', label: 'Cuma' },
        { value: 'saturday', label: 'Cumartesi' },
        { value: 'sunday', label: 'Pazar' }
    ];

    useEffect(() => {
        loadCategories();
    }, [organizationId]);

    useEffect(() => {
        if (product) {
            // Handle category_ids - support both old single category and new multi-category
            let categoryIds = [];
            if (product.category_ids && Array.isArray(product.category_ids)) {
                categoryIds = product.category_ids;
            } else if (product.category_id) {
                categoryIds = [product.category_id];
            } else if (categoryId) {
                categoryIds = [categoryId];
            }
            
            setFormData({
                name: product.name || '',
                description: product.description || '',
                price: product.price || '',
                currency: product.currency || 'TRY',
                category_ids: categoryIds,
                status: product.status || 'active',
                time_based_active: product.time_based_active || false,
                start_time: product.start_time || '',
                end_time: product.end_time || '',
                active_days: product.active_days ? (Array.isArray(product.active_days) ? product.active_days : JSON.parse(product.active_days)) : [],
                // Dietary tags
                is_vegan: product.is_vegan || false,
                is_vegetarian: product.is_vegetarian || false,
                is_gluten_free: product.is_gluten_free || false,
                contains_gluten: product.contains_gluten || false,
                is_spicy: product.is_spicy || false,
                spicy_level: product.spicy_level || 0,
                is_sweet: product.is_sweet || false,
                is_new: product.is_new || false,
                is_popular: product.is_popular || false,
                is_chef_recommended: product.is_chef_recommended || false,
                // Nutrition info
                calories: product.calories || '',
                protein: product.protein || '',
                carbs: product.carbs || '',
                fat: product.fat || '',
                allergens: product.allergens ? (Array.isArray(product.allergens) ? product.allergens : JSON.parse(product.allergens)) : [],
                preparation_time: product.preparation_time || '',
                // Discount fields
                discount_price: product.discount_price || '',
                discount_start_date: product.discount_start_date ? product.discount_start_date.split('T')[0] : '',
                discount_end_date: product.discount_end_date ? product.discount_end_date.split('T')[0] : '',
                discount_start_time: product.discount_start_time ? product.discount_start_time.substring(0, 5) : '',
                discount_end_time: product.discount_end_time ? product.discount_end_time.substring(0, 5) : '',
                discount_days: product.discount_days ? (Array.isArray(product.discount_days) ? product.discount_days : JSON.parse(product.discount_days)) : []
            });
            if (product.images) {
                // Set existing images from server
                setExistingImages(product.images.map(img => ({
                    id: img.id,
                    image_url: img.image_url,
                    video_url: img.video_url || null,
                    media_type: img.media_type || 'image',
                    is_primary: img.is_primary || false,
                    sort_order: img.sort_order || 0
                })));
                // Clear new uploads when editing
                setImages([]);
                setImagePreviews([]);
            } else {
                setExistingImages([]);
            }
        } else {
            resetForm();
        }
    }, [product, isOpen, categoryId]);

    const loadCategories = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/categories`);
            setCategories(response.data || []);
        } catch (error) {
            console.error('Load categories error:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            price: '',
            currency: 'TRY',
            category_ids: categoryId ? [categoryId] : [],
            status: 'active',
            time_based_active: false,
            start_time: '',
            end_time: '',
            active_days: [],
            // Dietary tags
            is_vegan: false,
            is_vegetarian: false,
            is_gluten_free: false,
            contains_gluten: false,
            is_spicy: false,
            spicy_level: 0,
            is_sweet: false,
            is_new: false,
            is_popular: false,
            is_chef_recommended: false,
            // Nutrition info
            calories: '',
            protein: '',
            carbs: '',
            fat: '',
            allergens: [],
            preparation_time: '',
            // Discount fields
            discount_price: '',
            discount_start_date: '',
            discount_end_date: '',
            discount_start_time: '',
            discount_end_time: '',
            discount_days: []
        });
        setImages([]);
        setImagePreviews([]);
        setExistingImages([]);
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDayToggle = (day) => {
        setFormData(prev => ({
            ...prev,
            active_days: prev.active_days.includes(day)
                ? prev.active_days.filter(d => d !== day)
                : [...prev.active_days, day]
        }));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate all files (images and videos)
        const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const validVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
        const validTypes = [...validImageTypes, ...validVideoTypes];
        const maxImageSize = 5 * 1024 * 1024; // 5MB for images
        const maxVideoSize = 50 * 1024 * 1024; // 50MB for videos
        const maxFiles = 10; // Maximum 10 files

        if (images.length + existingImages.length + files.length > maxFiles) {
            setError(`Maksimum ${maxFiles} dosya yükleyebilirsiniz.`);
            e.target.value = '';
            return;
        }

        const validFiles = [];
        files.forEach((file) => {
            if (!validTypes.includes(file.type)) {
                setError(`${file.name}: Geçersiz dosya formatı. Sadece resim ve video dosyaları desteklenir.`);
                return;
            }
            const isVideo = validVideoTypes.includes(file.type);
            const maxSize = isVideo ? maxVideoSize : maxImageSize;
            if (file.size > maxSize) {
                setError(`${file.name}: Dosya boyutu çok büyük. Maksimum ${isVideo ? '50MB' : '5MB'} olmalıdır.`);
                return;
            }
            validFiles.push(file);
        });

        if (validFiles.length === 0) {
            e.target.value = '';
            return;
        }

        setError(null);
        validFiles.forEach(file => {
            const isVideo = validVideoTypes.includes(file.type);
            if (isVideo) {
                // For videos, create a preview with video element
                const videoUrl = URL.createObjectURL(file);
                setImagePreviews(prev => [...prev, { type: 'video', url: videoUrl, file }]);
            } else {
                // For images, use FileReader
                const reader = new FileReader();
                reader.onerror = () => {
                    setError(`${file.name}: Dosya okunurken bir hata oluştu.`);
                };
                reader.onloadend = () => {
                    setImagePreviews(prev => [...prev, { type: 'image', url: reader.result, file }]);
                };
                reader.readAsDataURL(file);
            }
        });
        setImages(prev => [...prev, ...validFiles]);
        e.target.value = ''; // Reset input to allow selecting same file again
    };

    // Remove new image (before upload)
    const removeImage = (index) => {
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setImages(prev => prev.filter((_, i) => i !== index));
    };
    
    // Delete existing image from server
    const deleteExistingImage = async (imageId) => {
        if (!product || !product.id) return;
        
        try {
            await apiRequest(`/organizations/${organizationId}/products/${product.id}/images/${imageId}`, {
                method: 'DELETE'
            });
            setExistingImages(prev => prev.filter(img => img.id !== imageId));
        } catch (err) {
            setError(err.message || 'Görsel silinirken bir hata oluştu');
        }
    };
    
    // Set primary image
    const setPrimaryImage = async (imageId) => {
        if (!product || !product.id) return;
        
        try {
            await apiRequest(`/organizations/${organizationId}/products/${product.id}/images/${imageId}/primary`, {
                method: 'PUT'
            });
            setExistingImages(prev => prev.map(img => ({
                ...img,
                is_primary: img.id === imageId
            })));
        } catch (err) {
            setError(err.message || 'Ana görsel ayarlanırken bir hata oluştu');
        }
    };
    
    // Handle drag end for existing images
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        
        if (over && active.id !== over.id) {
            const oldIndex = existingImages.findIndex(img => img.id === active.id);
            const newIndex = existingImages.findIndex(img => img.id === over.id);
            
            const newImages = arrayMove(existingImages, oldIndex, newIndex);
            setExistingImages(newImages);
            
            // Update sort order on server
            if (product && product.id) {
                try {
                    const updates = newImages.map((img, index) => ({
                        id: img.id,
                        sort_order: index
                    }));
                    await apiRequest(`/organizations/${organizationId}/products/${product.id}/images/reorder`, {
                        method: 'PUT',
                        body: JSON.stringify({ images: updates }),
                        headers: { 'Content-Type': 'application/json' }
                    });
                } catch (err) {
                    console.error('Reorder error:', err);
                    // Revert on error
                    setExistingImages(existingImages);
                }
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate category selection
        if (!formData.category_ids || formData.category_ids.length === 0) {
            setError('En az bir kategori seçmelisiniz');
            return;
        }
        
        setLoading(true);
        setError(null);

        try {
            // First, create or update product (without images)
            // Prepare data - convert arrays to JSON strings for API
            const productData = {
                ...formData,
                // Send category_ids as array (backend will handle many-to-many)
                category_ids: formData.category_ids,
                // Keep backward compatibility - set primary category_id
                category_id: formData.category_ids[0],
                active_days: JSON.stringify(formData.active_days || []),
                allergens: JSON.stringify(formData.allergens || []),
                discount_days: JSON.stringify(formData.discount_days || []),
                // Convert empty strings to null for numeric fields
                calories: formData.calories || null,
                protein: formData.protein || null,
                carbs: formData.carbs || null,
                fat: formData.fat || null,
                preparation_time: formData.preparation_time || null,
                spicy_level: formData.is_spicy ? (formData.spicy_level || 1) : 0,
                // Discount fields
                discount_price: formData.discount_price || null,
                discount_start_date: formData.discount_start_date || null,
                discount_end_date: formData.discount_end_date || null,
                discount_start_time: formData.discount_start_time || null,
                discount_end_time: formData.discount_end_time || null
            };

            const url = product
                ? `/organizations/${organizationId}/products/${product.id}`
                : `/organizations/${organizationId}/products`;

            const method = product ? 'PUT' : 'POST';

            const response = await apiRequest(url, {
                method,
                body: JSON.stringify(productData),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const productId = product ? product.id : response.data.id;

            // Then, upload images if any (only for new products or if images are added)
            if (images.length > 0) {
                const formDataToSend = new FormData();
                images.forEach((image, index) => {
                    formDataToSend.append('images', image);
                });
                // Mark first image as primary (backend expects is_primary with index)
                formDataToSend.append('is_primary', '0');

                await apiRequest(`/organizations/${organizationId}/products/${productId}/images`, {
                    method: 'POST',
                    body: formDataToSend
                });
            }

            if (onSuccess) {
                onSuccess();
            }
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message || 'Ürün kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {product ? 'Ürün Düzenle' : 'Yeni Ürün'}
                        </h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Category Multi-Select */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Kategoriler * <span className="text-xs text-gray-400">(Birden fazla seçebilirsiniz)</span>
                            </label>
                            <CategoryMultiSelect
                                categories={categories}
                                selectedIds={formData.category_ids}
                                onChange={(ids) => setFormData(prev => ({ ...prev, category_ids: ids }))}
                            />
                            {formData.category_ids.length === 0 && (
                                <p className="mt-1 text-xs text-red-500">En az bir kategori seçmelisiniz</p>
                            )}
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                Ürün Adı *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Açıklama
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Price */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                                    Fiyat *
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    id="price"
                                    name="price"
                                    required
                                    value={formData.price}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                                    Para Birimi
                                </label>
                                <select
                                    id="currency"
                                    name="currency"
                                    value={formData.currency}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="TRY">TRY</option>
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                </select>
                            </div>
                        </div>

                        {/* Discount Section - şu an için gizli, kod korunuyor */}
                        {false && (
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <span>🏷️</span> İndirim Ayarları
                            </h3>
                            
                            {/* Discount Price */}
                            <div className="mb-4">
                                <label htmlFor="discount_price" className="block text-sm font-medium text-gray-700 mb-2">
                                    İndirimli Fiyat
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        step="0.01"
                                        id="discount_price"
                                        name="discount_price"
                                        value={formData.discount_price}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                    {formData.discount_price && formData.price && parseFloat(formData.discount_price) < parseFloat(formData.price) && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-green-600">
                                            %{Math.round(((formData.price - formData.discount_price) / formData.price) * 100)} indirim
                                        </span>
                                    )}
                                </div>
                                {formData.discount_price && formData.price && parseFloat(formData.discount_price) >= parseFloat(formData.price) && (
                                    <p className="text-xs text-red-500 mt-1">İndirimli fiyat normal fiyattan düşük olmalıdır</p>
                                )}
                            </div>

                            {/* Date Range */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label htmlFor="discount_start_date" className="block text-xs font-medium text-gray-600 mb-1">
                                        Başlangıç Tarihi
                                    </label>
                                    <input
                                        type="date"
                                        id="discount_start_date"
                                        name="discount_start_date"
                                        value={formData.discount_start_date}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="discount_end_date" className="block text-xs font-medium text-gray-600 mb-1">
                                        Bitiş Tarihi
                                    </label>
                                    <input
                                        type="date"
                                        id="discount_end_date"
                                        name="discount_end_date"
                                        value={formData.discount_end_date}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                            </div>

                            {/* Time Range */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label htmlFor="discount_start_time" className="block text-xs font-medium text-gray-600 mb-1">
                                        Başlangıç Saati
                                    </label>
                                    <input
                                        type="time"
                                        id="discount_start_time"
                                        name="discount_start_time"
                                        value={formData.discount_start_time}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="discount_end_time" className="block text-xs font-medium text-gray-600 mb-1">
                                        Bitiş Saati
                                    </label>
                                    <input
                                        type="time"
                                        id="discount_end_time"
                                        name="discount_end_time"
                                        value={formData.discount_end_time}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                            </div>

                            {/* Discount Days */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">
                                    İndirim Günleri (boş bırakılırsa her gün)
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {days.map(day => (
                                        <label 
                                            key={`discount_${day.value}`}
                                            className={`flex items-center justify-center gap-1 p-2 rounded-lg cursor-pointer border-2 transition-all text-xs ${
                                                formData.discount_days.includes(day.value) 
                                                    ? 'bg-green-50 text-green-700 border-green-300' 
                                                    : 'bg-gray-50 border-transparent hover:bg-gray-100'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.discount_days.includes(day.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            discount_days: [...prev.discount_days, day.value]
                                                        }));
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            discount_days: prev.discount_days.filter(d => d !== day.value)
                                                        }));
                                                    }
                                                }}
                                                className="sr-only"
                                            />
                                            <span>{day.label.substring(0, 3)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Images & Videos */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Görseller ve Videolar
                            </label>
                            
                            {/* Sortable existing images */}
                            {existingImages.length > 0 && (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={existingImages.map(img => img.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="grid grid-cols-4 gap-4 mb-4">
                                            {existingImages.map((img) => (
                                                <SortableImageItem
                                                    key={img.id}
                                                    image={img}
                                                    onDelete={() => deleteExistingImage(img.id)}
                                                    onSetPrimary={() => setPrimaryImage(img.id)}
                                                    getImageUrl={getImageUrl}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}
                            
                            {/* New uploads (not sortable yet, will be after upload) */}
                            {imagePreviews.length > 0 && (
                                <div className="grid grid-cols-4 gap-4 mb-4">
                                    {imagePreviews.map((preview, index) => (
                                        <div key={`new-${index}`} className="relative group">
                                            {preview.type === 'video' ? (
                                                <video
                                                    src={preview.url}
                                                    className="w-full h-24 object-cover rounded-lg"
                                                    controls={false}
                                                    muted
                                                />
                                            ) : (
                                                <img
                                                    src={preview.url}
                                                    alt={`Preview ${index + 1}`}
                                                    className="w-full h-24 object-cover rounded-lg"
                                                    onError={(e) => {
                                                        console.error('Image preview error:', e);
                                                        setError(`Görsel ${index + 1} önizlemesi yüklenemedi.`);
                                                    }}
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(index)}
                                                    className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-700"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            {preview.type === 'video' && (
                                                <span className="absolute top-1 left-1 bg-purple-600 text-white text-xs px-2 py-1 rounded">
                                                    🎥 Video
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            <input
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleImageChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Resimler (max 5MB) ve videolar (max 50MB) yükleyebilirsiniz. Sürükle-bırak ile sıralayabilir, ana görsel seçebilirsiniz.
                            </p>
                        </div>

                        {/* Dietary Tags Section */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <span>🏷️</span> Ürün Özellikleri
                            </h3>
                            <div className="grid grid-cols-3 gap-2">
                                {DIETARY_TAGS.map(tag => (
                                    <label 
                                        key={tag.key}
                                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border-2 transition-all ${
                                            formData[tag.key] 
                                                ? `${tag.color} border-current` 
                                                : 'bg-gray-50 border-transparent hover:bg-gray-100'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            name={tag.key}
                                            checked={formData[tag.key]}
                                            onChange={handleChange}
                                            className="sr-only"
                                        />
                                        <span className="text-lg">{tag.icon}</span>
                                        <span className="text-xs font-medium">{tag.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Spicy Level (if is_spicy) */}
                        {formData.is_spicy && (
                            <div>
                                <label htmlFor="spicy_level" className="block text-sm font-medium text-gray-700 mb-2">
                                    Acılık Seviyesi (1-5) 🌶️
                                </label>
                                <input
                                    type="range"
                                    id="spicy_level"
                                    name="spicy_level"
                                    min="1"
                                    max="5"
                                    value={formData.spicy_level || 1}
                                    onChange={handleChange}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>Hafif</span>
                                    <span className="text-lg">{'🌶️'.repeat(formData.spicy_level || 1)}</span>
                                    <span>Çok Acı</span>
                                </div>
                            </div>
                        )}

                        {/* Nutrition Info */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <span>📊</span> Besin Değerleri (İsteğe Bağlı)
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <label htmlFor="calories" className="block text-xs font-medium text-gray-600 mb-1">
                                        Kalori (kcal)
                                    </label>
                                    <input
                                        type="number"
                                        id="calories"
                                        name="calories"
                                        value={formData.calories}
                                        onChange={handleChange}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="protein" className="block text-xs font-medium text-gray-600 mb-1">
                                        Protein (g)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        id="protein"
                                        name="protein"
                                        value={formData.protein}
                                        onChange={handleChange}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="carbs" className="block text-xs font-medium text-gray-600 mb-1">
                                        Karbonhidrat (g)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        id="carbs"
                                        name="carbs"
                                        value={formData.carbs}
                                        onChange={handleChange}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="fat" className="block text-xs font-medium text-gray-600 mb-1">
                                        Yağ (g)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        id="fat"
                                        name="fat"
                                        value={formData.fat}
                                        onChange={handleChange}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Preparation Time */}
                        <div>
                            <label htmlFor="preparation_time" className="block text-sm font-medium text-gray-700 mb-2">
                                Hazırlama Süresi (dakika) ⏱️
                            </label>
                            <input
                                type="number"
                                id="preparation_time"
                                name="preparation_time"
                                value={formData.preparation_time}
                                onChange={handleChange}
                                placeholder="15"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Allergens */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <span>⚠️</span> Alerjenler (İsteğe Bağlı)
                            </h3>
                            <div className="grid grid-cols-3 gap-2">
                                {ALLERGENS.map(allergen => (
                                    <label 
                                        key={allergen.key}
                                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border-2 transition-all ${
                                            formData.allergens?.includes(allergen.key) 
                                                ? 'bg-red-50 text-red-700 border-red-300' 
                                                : 'bg-gray-50 border-transparent hover:bg-gray-100'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={formData.allergens?.includes(allergen.key)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        allergens: [...(prev.allergens || []), allergen.key]
                                                    }));
                                                } else {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        allergens: (prev.allergens || []).filter(a => a !== allergen.key)
                                                    }));
                                                }
                                            }}
                                            className="sr-only"
                                        />
                                        <span className="text-xs font-medium">{allergen.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Time Based Active */}
                        <div className="border-t border-gray-200 pt-4">
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    name="time_based_active"
                                    checked={formData.time_based_active}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Saat bazlı aktif/pasif kontrolü
                                </span>
                            </label>
                        </div>

                        {/* Time Range */}
                        {formData.time_based_active && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-2">
                                            Başlangıç Saati
                                        </label>
                                        <input
                                            type="time"
                                            id="start_time"
                                            name="start_time"
                                            value={formData.start_time}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mb-2">
                                            Bitiş Saati
                                        </label>
                                        <input
                                            type="time"
                                            id="end_time"
                                            name="end_time"
                                            value={formData.end_time}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                {/* Active Days */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Aktif Günler
                                    </label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {days.map(day => (
                                            <label key={day.value} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.active_days.includes(day.value)}
                                                    onChange={() => handleDayToggle(day.value)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700">{day.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Status */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                Durum
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="active">Aktif</option>
                                <option value="inactive">Pasif</option>
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProductFormModal;

